import { Box } from '@mui/material';
import { React } from 'Imports';
import { useMemo } from 'react';
import CalendarNavigation from '$Components/FilterComponents/DateRangePicker/CalendarNavigation';
import { Calendar } from 'react-date-range';
import { isEncompass } from '$Utilities/integrationPlatform';

const styles = isEncompass
    ? (require('./SingleDatePickerCalendar_Encompass.scss') as {
          container: string;
          calendar: string;
      })
    : (require('./SingleDatePickerCalendar_Geotab.scss') as {
          container: string;
          calendar: string;
      });

interface ISingleDatePickerCalendarProps {
    date?: Date;
    onDateChange: (d: Date) => void;
    availableDates?: Date[];
}

export const SingleDatePickerCalendar = (props: ISingleDatePickerCalendarProps) => {
    const { date, onDateChange, availableDates } = props;
    const availableDateKeys = useMemo(
        () => new Set((availableDates ?? []).map((d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`)),
        [availableDates],
    );

    const dayContentRenderer = (day: Date) => {
        const key = `${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`;
        const highlighted = availableDateKeys.has(key);
        return highlighted ? <span className="has-footage">{day.getDate()}</span> : null;
    };

    const customNavigatorRenderer = (focusedDate: Date, changeShownDate: (date: Date) => void) => (
        <CalendarNavigation
            currentMonth={focusedDate.getMonth()}
            currentYear={focusedDate.getFullYear()}
            onMonthChange={(month) => {
                const newDate = new Date(focusedDate);
                newDate.setMonth(month);
                changeShownDate(newDate);
            }}
            onYearChange={(year) => {
                const newDate = new Date(focusedDate);
                newDate.setFullYear(year);
                changeShownDate(newDate);
            }}
            onPreviousMonth={() => {
                const newDate = new Date(focusedDate);
                newDate.setMonth(focusedDate.getMonth() - 1);
                changeShownDate(newDate);
            }}
            onNextMonth={() => {
                const newDate = new Date(focusedDate);
                newDate.setMonth(focusedDate.getMonth() + 1);
                changeShownDate(newDate);
            }}
        />
    );

    return (
        <Box className={styles.container} data-testid={'single-date-picker-calendar-container'}>
            <Box
                className={styles.calendar}
                id={'single-date-picker-calendar' + (isEncompass ? '-encompass' : '-geotab')}
                data-testid={'single-date-picker-calendar'}
            >
                <Calendar
                    date={date}
                    onChange={onDateChange}
                    disabledDay={(d: Date) => d > new Date()}
                    maxDate={new Date()}
                    dragSelectionEnabled={false}
                    showDateDisplay={false}
                    showMonthAndYearPickers={false}
                    navigatorRenderer={customNavigatorRenderer}
                    dayContentRenderer={dayContentRenderer}
                />
            </Box>
        </Box>
    );
};

export default SingleDatePickerCalendar;
